import { Spacing } from '@persona-id/design-tokens';
import { isEqual } from 'lodash';
import { FormEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { EventOrValueHandler, Field, InjectedFormProps, reduxForm } from 'redux-form';
import styled from 'styled-components';

import { Address } from 'dashboard/components/AddressInput';
import InputField from 'dashboard/components/FormControls/InputField';
import SelectField, { Option } from 'dashboard/components/FormControls/SelectField';
import LocationSearchInput from 'dashboard/components/LocationSearchInput';
import { RootState } from 'dashboard/redux/store';
import {
  ADDRESS_FORMATS_BY_COUNTRY_CODE,
  AddressField,
  AddressFormat,
  DEFAULT_ADDRESS_FORMAT,
  DEFAULT_LABELS,
} from 'lib/constants/addresses';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';
import PiiAttributes from 'lib/interfaces/PiiAttributes';

const Row = styled('div')`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: repeat(6, 1fr);
`;

const ShortCol = styled('div')`
  grid-column: span 2;
`;

const LongCol = styled('div')`
  grid-column: span 4;
`;

const FullCol = styled('div')`
  grid-column: span 6;
`;

// The original AddressInput that we were using here used 'street1', 'street2', etc.
// as opposed to 'address-street-1', 'address-street-2', etc. It looks like we are
// moving in the direction of using 'street1', 'street2' instead of prefixing them with
// 'address', and so to be consistent with that future state (and to be consistent for
// backward compatibility), we want to use 'street1' instead of 'address-street-1', which
// is what the LocationSearchInput is coded to look for.
// The LocationSearchInput automatically looks for fields with name PiiAttributes.AddressStreet1,
// etc., and so we need this mapping to correctly update fields when the user
// selects a suggested address.
// THIS IS SOMETHING THAT SHOULD IDEALLY BE CLEANED UP LONGER TERM.
const FIELD_NAME_MAP: Partial<Record<PiiAttributes, string>> = {
  [PiiAttributes.AddressStreet1]: 'street1',
  [PiiAttributes.AddressStreet2]: 'street2',
  [PiiAttributes.AddressCity]: 'city',
  [PiiAttributes.AddressSubdivision]: 'subdivision',
  [PiiAttributes.AddressPostalCode]: 'postalCode',
  [PiiAttributes.AddressCountryCode]: 'countryCode',
};

interface AddressInputAutocompleteProps {
  disabled?: boolean;
  onChange: (values: Address) => void;
}

const AddressInputAutocomplete = ({
  initialValues,
  disabled,
  change,
  onChange,
}: AddressInputAutocompleteProps &
  InjectedFormProps<AddressInputAutocompleteData, AddressInputAutocompleteProps>) => {
  const currentFormValues = useSelector((state: RootState) => {
    return state.form['addressAutoComplete']?.values ?? {};
  });

  useEffect(() => {
    if (!isEqual(currentFormValues, initialValues)) {
      onChange(currentFormValues);
    }
  }, [currentFormValues, initialValues, onChange]);

  const [countryCode, setCountryCode] = useState<CountryCode>(initialValues?.countryCode || 'US');

  const boundChangeFormValue = (field: PiiAttributes, value: string) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    change(FIELD_NAME_MAP[field], value);
  };

  const addressFormat = ADDRESS_FORMATS_BY_COUNTRY_CODE[countryCode] || DEFAULT_ADDRESS_FORMAT;

  const renderCountryOrderedAddress = () => {
    const fieldComponents: Record<AddressField, JSX.Element> = {
      address1: (
        <Field
          autoComplete='address-line1'
          name='street1'
          disabled={disabled}
          placeholder={addressFormat.labels.address1 || DEFAULT_LABELS.address1}
          type='text'
          boundChangeFormValue={boundChangeFormValue}
          // TODO: [PER-2429] Make LocationSearchInput not ThemedFormInput dependent
          component={
            addressFormat.autocompletionField === 'address1' ? LocationSearchInput : InputField
          }
          {...(addressFormat.autocompletionField === 'address1' ? { theme: {} } : {})}
        />
      ),
      address2: (
        <Field
          autoComplete='address-line2'
          name='street2'
          disabled={disabled}
          type='text'
          showError={true}
          placeholder={addressFormat.labels.address2 || DEFAULT_LABELS.address2}
          // TODO: [PER-2429] Make LocationSearchInput not ThemedFormInput dependent
          component={
            addressFormat.autocompletionField === 'address2' ? LocationSearchInput : InputField
          }
          {...(addressFormat.autocompletionField === 'address2' ? { theme: {} } : {})}
        />
      ),
      city: (
        <Field
          autoComplete='address-level2'
          name='city'
          disabled={disabled}
          type='text'
          placeholder={addressFormat.labels.city || DEFAULT_LABELS.city}
          component={InputField}
        />
      ),
      postalCode: (
        <Field
          autoComplete='postal-code'
          name='postalCode'
          disabled={disabled}
          placeholder={addressFormat.labels.postalCode || DEFAULT_LABELS.postalCode}
          type='text'
          boundChangeFormValue={boundChangeFormValue}
          // TODO: [PER-2429] Make LocationSearchInput not ThemedFormInput dependent
          component={
            addressFormat.autocompletionField === 'postalCode' ? LocationSearchInput : InputField
          }
          {...(addressFormat.autocompletionField === 'postalCode' ? { theme: {} } : {})}
        />
      ),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      subdivision: <SubdivisionFormField addressFormat={addressFormat} disabled={disabled} />,
    };

    return addressFormat.formatting.map((row, rowIndex) => (
      <div key={rowIndex}>
        <Row>
          {row.map((fieldName, index) => {
            const fieldComponent = fieldComponents[fieldName];
            if (row.length === 1) {
              return <FullCol key={`${countryCode}-${index}`}>{fieldComponent}</FullCol>;
            } else {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              if (fieldName === 'address2') {
                return <ShortCol key={`${countryCode}-${index}`}>{fieldComponent}</ShortCol>;
              } else {
                return <LongCol key={`${countryCode}-${index}`}>{fieldComponent}</LongCol>;
              }
            }
          })}
        </Row>
      </div>
    ));
  };

  return (
    <>
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <CountrySelector disabled={disabled} setCountryCode={setCountryCode} />
      {renderCountryOrderedAddress()}
    </>
  );
};

const CountrySelector = ({
  disabled,
  setCountryCode,
}: {
  disabled: boolean;
  setCountryCode: (countryCode: CountryCode) => void;
}) => {
  return (
    <Field
      autoComplete='address-country-code'
      name='countryCode'
      component={SelectField}
      disabled={disabled}
      id={PiiAttributes.CountryCode}
      type='text'
      placeholder='Country code'
      /**
       * redux-form allows an input's onChange to be eventOrValue
       * but the onChange on the Field is inflexible.
       * We typecast this because we know the SelectField to always return a value.
       */
      onChange={
        ((value: string) => {
          setCountryCode(value as CountryCode);
        }) as EventOrValueHandler<FormEvent<HTMLSelectElement>>
      }
    >
      {Object.keys(COUNTRIES)
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        .map((code: keyof typeof COUNTRIES) => [COUNTRIES[code], code])
        .sort((a, b) => a[0].toLowerCase().localeCompare(b[0].toLowerCase()))
        .map(([countryName, code]) => (
          <Option key={code} value={code}>
            {countryName}
          </Option>
        ))}
    </Field>
  );
};

const SubdivisionFormField = ({
  addressFormat,
  disabled,
}: {
  addressFormat: AddressFormat;
  disabled: boolean;
}) => {
  const label = addressFormat.labels.subdivision || DEFAULT_LABELS.subdivision;

  if (addressFormat.subdivisions.length) {
    return (
      <Field
        autoComplete='address-level1'
        name='subdivision'
        component={SelectField}
        disabled={disabled}
        type='text'
        placeholder={label}
      >
        {addressFormat.subdivisions.map((subdivision) => (
          <Option key={subdivision.code} value={subdivision.untranslatedName}>
            {subdivision.code}
          </Option>
        ))}
      </Field>
    );
  } else {
    return (
      <Field
        autoComplete='address-level1'
        name='subdivision'
        component={InputField}
        disabled={disabled}
        type='text'
        placeholder={label}
      />
    );
  }
};

export interface AddressInputAutocompleteData {
  street1?: string;
  street2?: string;
  city?: string;
  subdivision?: string;
  postalCode?: string;
  countryCode?: CountryCode;
}

export default reduxForm<AddressInputAutocompleteData, AddressInputAutocompleteProps>({
  form: 'addressAutoComplete',
})(AddressInputAutocomplete);
